import { useCallback, useEffect, useState } from "react";
import Modal from "react-modal";
import { CiCirclePlus } from "react-icons/ci";
import { FaRegTrashAlt } from "react-icons/fa";
import { HiSparkles } from "react-icons/hi2";
import { ActiveButton, EditButton, Button } from "../../components/buttons";
import { Image, Input, Textarea } from "../../components/inputs";
import ToggleButton from "../../components/toggle-button";
import { AWS_DIRECTORIES } from "../../constants/Common";
import useData from "../../hooks/useData";
import { generateObjectId } from "../../utilities/helpers";
import { BASE, useFetch } from "../../utilities/apis";

const ViewDepartments = () => {
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState("");
  const [departments, setDepartments] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedDepartment, setEditedDepartment] = useState({});
  const [imageDepartment, setImageDepartment] = useState({});
  const [open, setOpen] = useState(false);
  const [aiPromptModalOpen, setAiPromptModalOpen] = useState(false);
  const [aiPromptDepartment, setAiPromptDepartment] = useState({});
  const [aiPrompts, setAiPrompts] = useState([]);
  const { getExams, getDepartmentByExam } = useData();
  const { update } = useFetch();

  const modalStyles = {
    content: {
      width: "40%",
      top: "53%",
      zIndex: 1,
      maxHeight: "calc(100vh - 7rem)",
      position: "fixed",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      boxShadow: "rgba(100, 100, 111, 0.2) 0px 7px 29px 0px",
    },
  };

  useEffect(() => {
    const fetchExams = async () => {
      const exams = await getExams();
      if (exams) setExams(exams);
    };

    fetchExams();
  }, []);

  useEffect(() => {
    if (selectedExam) {
      const fetchDepartments = async () => {
        const departments = await getDepartmentByExam(selectedExam);
        if (departments) setDepartments(departments);
      };

      fetchDepartments();
    }
  }, [selectedExam]);

  const handleEditClick = (index, department) => {
    if (editingIndex === null) {
      setEditingIndex(index);
      setEditedDepartment({ code: department.code, title: department.title, order: department.order ?? 0 });
    } else if (editingIndex === index) {
      handleUpdate(department, editedDepartment, true);
    } else {
      setEditingIndex(index);
      setEditedDepartment({ code: department.code, title: department.title, order: department.order ?? 0 });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedDepartment({ ...editedDepartment, [name]: name === "order" ? Number(value) : value });
  };

  const handleUpdate = (department, data, edit = false) => {
    update(
      `${BASE}update/department/?id=${department._id}`,
      data,
      (error, res) => {
        if (error) return console.log(error);
        const clone = [...departments];
        const index = clone.findIndex((e) => e._id === department._id);
        clone[index] = res;
        setDepartments(clone);

        if (edit) {
          setEditingIndex(null);
          setEditedDepartment({});
        }
      }
    );
  };

  const handleImageModal = (department) => {
    setImageDepartment(department);
    setOpen(true);
  };

  const closeImageModal = () => {
    setImageDepartment({});
    setOpen(false);
  };

  const handleImageChange = (link) => {
    const data = { image: link };
    handleUpdate(imageDepartment, data);
    setImageDepartment({ ...imageDepartment, image: link });
  };

  const handleAiPromptModal = (department) => {
    setAiPromptDepartment(department);
    setAiPrompts(
      (department.aiPrompts ?? []).map((item) => ({
        id: item._id ?? generateObjectId(),
        text: item.text,
        prompt: item.prompt,
      }))
    );
    setAiPromptModalOpen(true);
  };

  const closeAiPromptModal = () => {
    setAiPromptDepartment({});
    setAiPrompts([]);
    setAiPromptModalOpen(false);
  };

  const addAiPromptInput = useCallback(() => {
    setAiPrompts((prev) => [
      ...prev,
      { id: generateObjectId(), text: "", prompt: "" },
    ]);
  }, []);

  const onAiPromptTextChange = useCallback((value, id) => {
    setAiPrompts((prev) => {
      const clone = [...prev];
      const index = clone.findIndex((e) => e.id === id);
      if (index !== -1) clone[index].text = value;
      return clone;
    });
  }, []);

  const onAiPromptChange = useCallback((value, id) => {
    setAiPrompts((prev) => {
      const clone = [...prev];
      const index = clone.findIndex((e) => e.id === id);
      if (index !== -1) clone[index].prompt = value;
      return clone;
    });
  }, []);

  const deleteAiPromptInput = useCallback((id) => {
    setAiPrompts((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const saveAiPrompts = () => {
    const data = {
      aiPrompts: aiPrompts.map(({ text, prompt }) => ({ text, prompt })),
    };

    update(
      `${BASE}update/department/?id=${aiPromptDepartment._id}`,
      data,
      (error, res) => {
        if (error) return console.log(error);
        const clone = [...departments];
        const index = clone.findIndex((e) => e._id === aiPromptDepartment._id);
        if (index !== -1) clone[index] = res;
        setDepartments(clone);
        closeAiPromptModal();
      }
    );
  };

  return (
    <div>
      <form>
        <div className="mb-3">
          <label htmlFor="exam" className="form-label">
            Select exam
          </label>
          <select
            className="form-select"
            id="exam"
            value={selectedExam}
            onChange={(e) => setSelectedExam(e.target.value)}
          >
            <option value="">Select exam</option>
            {exams.map((exam, index) => {
              return (
                <option key={index} value={exam._id}>
                  {exam.title}
                </option>
              );
            })}
          </select>
        </div>
      </form>

      <table className="table">
        <thead>
          <tr>
            <th scope="col">#</th>
            <th scope="col">Rank</th>
            <th scope="col">Department code</th>
            <th scope="col">Department title</th>
            <th scope="col">Ask AI</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {departments.map((department, index) => {
            return (
              <tr key={index}>
                <th scope="row">{index + 1}</th>

                <td>
                  {editingIndex === index ? (
                    <input
                      type="number"
                      name="order"
                      className="table-control"
                      style={{ width: "70px" }}
                      value={editedDepartment.order}
                      onChange={handleInputChange}
                    />
                  ) : (
                    department.order ?? 0
                  )}
                </td>

                <td>
                  {editingIndex === index ? (
                    <input
                      type="text"
                      name="code"
                      className="table-control"
                      value={editedDepartment.code}
                      onChange={handleInputChange}
                    />
                  ) : (
                    department.code
                  )}
                </td>

                <td>
                  {editingIndex === index ? (
                    <input
                      type="text"
                      name="title"
                      className="table-control"
                      value={editedDepartment.title}
                      onChange={handleInputChange}
                    />
                  ) : (
                    department.title
                  )}
                </td>

                <td>
                  <ToggleButton
                    value={department.askAI ?? false}
                    onChange={(val) =>
                      handleUpdate(department, { askAI: val })
                    }
                  />
                </td>

                <td>
                  <EditButton
                    className="me-3"
                    editing={editingIndex === index}
                    onClick={() => handleEditClick(index, department)}
                  />

                  <ActiveButton
                    className="me-3"
                    active={department.display}
                    onClick={() =>
                      handleUpdate(department, { display: !department.display })
                    }
                  />

                  <span
                    className="action"
                    onClick={() => handleImageModal(department)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      className="bi bi-image-fill"
                      viewBox="0 0 16 16"
                    >
                      <path d="M.002 3a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-12a2 2 0 0 1-2-2zm1 9v1a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062zm5-6.5a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0" />
                    </svg>
                  </span>

                  <span
                    className="action ms-3"
                    onClick={() => handleAiPromptModal(department)}
                  >
                    <HiSparkles size={16} title="AI Prompts" />
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <Modal isOpen={open} onRequestClose={closeImageModal} style={modalStyles}>
        <h4>{imageDepartment.code}</h4>

        <div className="my-3">
          {imageDepartment.image ? (
            <img
              src={imageDepartment.image}
              alt={imageDepartment.code}
              className="img-thumbnail"
            />
          ) : (
            "No image"
          )}
        </div>

        <div>
          <Image
            directory={AWS_DIRECTORIES.DEPARTMENT}
            onUpload={handleImageChange}
          />
        </div>
      </Modal>

      <Modal
        isOpen={aiPromptModalOpen}
        onRequestClose={closeAiPromptModal}
        style={modalStyles}
      >
        <h4>{aiPromptDepartment.code} - AI Prompts</h4>

        <div className="d-flex align-items-center gap-2 my-3">
          <h6 className="mb-0">Prompts</h6>
          <CiCirclePlus
            onClick={addAiPromptInput}
            className="cursor-pointer !text-primary"
            size={24}
          />
        </div>

        <div style={{ maxHeight: "50vh", overflowY: "auto" }}>
          {aiPrompts.map(({ id, text, prompt }) => (
            <div key={id} className="d-flex gap-3 align-items-start border p-3 mb-2">
              <div className="flex-grow-1">
                <Input
                  value={text}
                  onChange={(value) => onAiPromptTextChange(value, id)}
                  label="Button Text"
                  placeholder="e.g. Learn the Concept"
                />
                <Textarea
                  value={prompt}
                  onChange={(value) => onAiPromptChange(value, id)}
                  label="Prompt"
                  placeholder="Prompt sent to the AI"
                  rows={3}
                />
              </div>

              <FaRegTrashAlt
                onClick={() => deleteAiPromptInput(id)}
                className="cursor-pointer mt-2"
              />
            </div>
          ))}
        </div>

        <div className="text-end mt-3">
          <Button onClick={saveAiPrompts}>Save</Button>
        </div>
      </Modal>
    </div>
  );
};

export default ViewDepartments;
